import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import noop from 'lodash/noop';
import { graphql, useFragment, useRefetchableFragment } from 'react-relay';
import { useRefetchWhenIsSelected } from '@jira/navigation-apps__routing-hooks/src/useRefetchWhenIsSelected.tsx';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { useSubscribeAndUpdateFavorites } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/services/set-favorite/index.tsx';
import { useStableItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/utils/use-stable-items/index.tsx';
import { useReorderableStarredItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/utils/use-reorderable-starred-items.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { MENU_ID_ROADMAPS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { NestedItemDropTarget } from '@atlassian/navigation-drag-and-drop/nested-item-drop-target';
import { RoadmapItem } from './RoadmapItem.tsx';
import { useRoadmapReorderingIds } from './common/utils/useRoadmapReorderingIds.tsx';
import type {
	RoadmapsContent$data,
	RoadmapsContent$key,
} from './__generated__/RoadmapsContent.graphql.ts';
import type { RoadmapsContent_navigationCustomisation$key } from './__generated__/RoadmapsContent_navigationCustomisation.graphql.ts';
import QUERY, {
	type Nav4RoadmapsContentQuery,
} from './__generated__/Nav4RoadmapsContentQuery.graphql.ts';
import { RoadmapsEmptyContent } from './RoadmapsEmptyContent.tsx';
import { useDragAndDropReorderingAnalytics } from './useDragAndDropReorderingAnalytics.tsx';
import { useNavigationCollectionsActions } from '@jira/polaris__component-navigation-collections-store/src/useNavigationCollectionsActions.tsx';

const MAX_RECENT_ROADMAPS = 5;
const MAX_FAVORITE_ROADMAPS = 15;

const recentMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.common.content.recent',
	defaultMessage: 'Recent',
	description: 'Message for recent heading in the roadmaps menu items',
});

const starredMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.common.content.starred',
	defaultMessage: 'Starred',
	description: 'Message for starred heading in the roadmap menu items',
});

type RoadmapsContentProps = {
	queryRef: RoadmapsContent$key;
	navigationCustomisationFragment?: RoadmapsContent_navigationCustomisation$key;
};

export function RoadmapsContent({
	queryRef,
	navigationCustomisationFragment,
}: RoadmapsContentProps): React.JSX.Element | null {
	const cloudId = useCloudId();
	const { isSelectedPath } = useSidebarNav4();

	const [jira, refetch] = useRefetchableFragment<Nav4RoadmapsContentQuery, RoadmapsContent$key>(
		graphql`
			fragment RoadmapsContent on Query
			@refetchable(queryName: "Nav4RoadmapsContentQuery")
			@argumentDefinitions(currentURL: { type: URL }) {
				jira @required(action: THROW) {
					roadmapFavourites: favourites(
						cloudId: $cloudId
						filter: { type: JPD_COLLECTION, sort: { order: DESC } }
						first: 15
					) @required(action: THROW) {
						__id
						pageInfo {
							hasNextPage
						}
						edges {
							node @required(action: THROW) {
								... on JiraProductDiscoveryCollection {
									id @required(action: THROW)
									collectionId
									title
									...RoadmapItem_collection @alias
								}
							}
						}
					}
					roadmapRecents: recentItems(
						cloudId: $cloudId
						currentURL: $currentURL
						types: [JPD_COLLECTION]
						first: 20
					) @required(action: THROW) {
						__id
						edges {
							node @required(action: THROW) {
								... on JiraProductDiscoveryCollection {
									id @required(action: THROW)
									collectionId
									title
									...RoadmapItem_collection @alias
								}
							}
						}
					}
				}
			}
		`,
		queryRef,
	);

	useRefetchWhenIsSelected({ cloudId, isSelectedPath, QUERY, refetch, menuId: MENU_ID_ROADMAPS });

	const refetchOnChange = useCallback(
		({ onComplete }: { onComplete: () => void }) => {
			refetch({ cloudId }, { fetchPolicy: 'store-and-network', onComplete });
		},
		[cloudId, refetch],
	);
	useSubscribeAndUpdateFavorites({
		entityTypename: 'JiraProductDiscoveryCollection',
		favouriteConnectionId: jira.jira.roadmapFavourites.__id || '',
		recentConnectionId: jira.jira.roadmapRecents.__id || '',
		refetch: refetchOnChange,
	});

	const { addDeleteListener, removeDeleteListener } = useNavigationCollectionsActions();
	const refetchOnChangeRef = useRef(refetchOnChange);
	refetchOnChangeRef.current = refetchOnChange;
	useEffect(() => {
		const handleDeletion = () => refetchOnChangeRef.current({ onComplete: noop });
		addDeleteListener(handleDeletion);
		return () => {
			removeDeleteListener(handleDeletion);
		};
	}, [addDeleteListener, removeDeleteListener]);

	return (
		<RoadmapContentInner
			jira={jira.jira}
			navigationCustomisationFragment={navigationCustomisationFragment}
		/>
	);
}

type RoadmapContentInnerProps = {
	jira: RoadmapsContent$data['jira'];
	navigationCustomisationFragment?: RoadmapsContent_navigationCustomisation$key;
};

const RoadmapContentInner = (props: RoadmapContentInnerProps) => {
	const {
		navigationCustomisationFragment,
		jira: { roadmapFavourites, roadmapRecents },
	} = props;
	const navigationCustomisationData = useFragment(
		graphql`
			fragment RoadmapsContent_navigationCustomisation on SettingsNavigationCustomisation {
				sidebar {
					nodes {
						menuId
						visible
					}
				}
			}
		`,
		navigationCustomisationFragment,
	);

	const { getStableItems } = useStableItems();
	const { formatMessage } = useIntl();
	const starred = formatMessage(starredMessage);
	const recent = formatMessage(recentMessage);
	const { fireAnalyticsEvent } = useDragAndDropReorderingAnalytics();
	const { entityAri, ownerAri, group } = useRoadmapReorderingIds();

	type RoadmapEdge = NonNullable<NonNullable<typeof roadmapFavourites.edges>[number]>;
	type RoadmapNode = NonNullable<RoadmapEdge['node']>;

	const roadmapNodes: RoadmapNode[] = useMemo(
		() =>
			roadmapFavourites.edges?.reduce<RoadmapNode[]>((nodes, edge) => {
				if (edge?.node != null) {
					nodes.push(edge.node);
				}
				return nodes;
			}, []) ?? [],
		[roadmapFavourites.edges],
	);

	const lastOrder = useMemo(
		() =>
			navigationCustomisationData?.sidebar?.nodes?.map((node) => ({
				menuId: node?.menuId,
				visible: node?.visible,
			})),
		[navigationCustomisationData?.sidebar?.nodes],
	);

	const favorites = useReorderableStarredItems<RoadmapNode>({
		favourites: roadmapNodes,
		lastOrder,
		getId: (item) => item.id || '',
		getLabel: (item) => item.title?.toString() || '',
		getHasNextPage: () => Boolean(roadmapFavourites?.pageInfo?.hasNextPage),
		renderMenuItem: (edge, currentItems, currentOrder, item) => (
			<RoadmapItem
				key={edge.collectionId}
				collectionQueryRefFragment={edge.RoadmapItem_collection}
				currentItems={currentItems}
				currentOrder={currentOrder}
				item={item}
				isDraggable
			/>
		),
		maxItems: MAX_FAVORITE_ROADMAPS,
	});

	const recentItems = useMemo(() => {
		type RecentNode = NonNullable<NonNullable<typeof roadmapRecents.edges>[number]>['node'];

		const nodes: RecentNode[] = [];

		roadmapRecents.edges?.forEach((edge: NonNullable<typeof roadmapRecents.edges>[number]) => {
			if (edge && edge.node.id && !favorites.idSet.has(edge.node.id)) {
				nodes.push(edge.node);
			}
		});

		const limitedNodes = nodes.slice(0, MAX_RECENT_ROADMAPS);
		const stableOrderedNodes = getStableItems({ nodes: limitedNodes, idKey: 'id' });
		const list = stableOrderedNodes.map((node) => (
			<RoadmapItem
				key={node.collectionId}
				collectionQueryRefFragment={node.RoadmapItem_collection}
			/>
		));

		return { list, hasItems: Boolean(list.length) };
	}, [roadmapRecents, favorites.idSet, getStableItems]);

	if (!favorites.hasItems && !recentItems.hasItems) {
		return <RoadmapsEmptyContent />;
	}

	return (
		<>
			{favorites.hasItems && (
				<NestedItemDropTarget
					currentItems={favorites.currentItems}
					currentOrder={favorites.currentOrder}
					entityAri={entityAri}
					group={group}
					ownerAri={ownerAri}
					fireAnalyticsEvent={fireAnalyticsEvent}
				>
					<MenuListItem>
						<MenuSection>
							<MenuSectionHeading>{starred}</MenuSectionHeading>
							<MenuList>{favorites.list}</MenuList>
						</MenuSection>
					</MenuListItem>
				</NestedItemDropTarget>
			)}

			{recentItems.hasItems && (
				<MenuListItem>
					<MenuSection>
						<MenuSectionHeading>{recent}</MenuSectionHeading>
						<MenuList>{recentItems.list}</MenuList>
					</MenuSection>
				</MenuListItem>
			)}
		</>
	);
};
